
import Vue from 'vue';
import LoadingModal from './LoadingModal.vue';
import { ModalActions, Button, SelectBox, Icon } from '@descomplica/vue-components';
import { createDefaultLinkedinShareMessage } from '@/helpers/postgradMicrocertificate/linkedin';
import { getDexToken } from '@/helpers/get-tokens';
import { LocaleMessage } from 'vue-i18n';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { getCourseCategory } from '@/helpers/category';
import { CourseModel } from '@descomplica/dex-models';
import { LinkedinPostVisibility, PostMediaCategory } from '@/helpers/linkedin';
import { LinkedinService } from '@/services/Linkedin';
import SentryClient from '@/helpers/sentry';
import { PostgradEarlyGraduationTrackingInformation } from '@/models/postgradEarlyGraduation/PostgradEarlyGraduationModel';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';

interface Data {
  linkedinDescription: string;
  whoCanSee: { key: string; value: string } | {};
  optionsConfigurations: { key: string; value: string } | {};
  loadingShareMicrocertificate: boolean;
  messages: LocaleMessage;
  subtitle: LocaleMessage;
  editInformation: boolean;
}

export default Vue.extend({
  components: {
    ModalActions,
    Button,
    SelectBox,
    Icon,
    LoadingModal,
  },
  props: {
    disciplineName: {
      required: true,
      type: String,
    },
    course: {
      required: true,
      type: Object as Vue.PropType<CourseModel>,
    },
    linkedinOAuthCode: {
      type: String,
      required: true,
    },
    microcertificateImageUrl: {
      type: String,
      required: true,
    },
  },
  data(): Data {
    return {
      linkedinDescription: createDefaultLinkedinShareMessage(this.disciplineName),
      whoCanSee: {},
      optionsConfigurations: {},
      messages: [this.$t('postgradMicrocertificate.loadingModal.connectingToMicrocertificate')],
      subtitle: this.$t('postgradMicrocertificate.loadingModal.sharingMicrocertificate'),
      loadingShareMicrocertificate: false,
      editInformation: false,
    };
  },
  computed: {
    currentChimeraCourse(): ChimeraCourseModel {
      return this.$store.getters.getCurrentChimeraCourse;
    },
    earlyGraduationTrackingProps(): PostgradEarlyGraduationTrackingInformation {
      return this.$store.getters.earlyGraduationTrackings(this.currentChimeraCourse);
    },
  },
  created() {
    const options = this.createOptionsConfigurations();
    this.optionsConfigurations = options;
    this.whoCanSee = { ...options[0] };
    Analytics.trackEvent(EVENT_LABELS.modalViewed, {
      course: this.course.name,
      category: getCourseCategory(this.course),
      postgrad_microcertificate_linkedin_modal: true,
      ...this.earlyGraduationTrackingProps,
    });
  },
  methods: {
    updateOptionsInformation(updateContent: object) {
      this.whoCanSee = updateContent;
    },
    async shareMicrocertificate(): Promise<void> {
      this.trackShareButton();
      const dexToken = getDexToken();

      if (!dexToken || !this.microcertificateImageUrl) return;

      this.loadingShareMicrocertificate = true;

      try {
        await LinkedinService.sharePost(dexToken, {
          linkedinOAuthCode: this.linkedinOAuthCode,
          visibility: Object.values(this.whoCanSee)[0],
          content: this.linkedinDescription,
          imageSource: this.microcertificateImageUrl,
          postCategory: PostMediaCategory.IMAGE,
        });
        this.trackSharedInLinkedin('postgradMicrocertificateLinkedinShare');
        this.$emit('share', true);
      } catch (error) {
        this.trackSharedInLinkedin('postgradMicrocertificateLinkedinShareError');
        this.$emit('share', false);

        SentryClient.captureError(error);
      }
    },
    openEditSelection(): void {
      this.editInformation = true;
    },
    createOptionsConfigurations(): Array<object> {
      return [
        {
          key: LinkedinPostVisibility.PUBLIC,
          value: this.$tc('postgradMicrocertificate.linkedinModal.information.options.public'),
        },
        {
          key: LinkedinPostVisibility.CONNECTIONS,
          value: this.$tc('postgradMicrocertificate.linkedinModal.information.options.connections'),
        },
      ];
    },
    verifyIfTextWasChanged(): boolean {
      return this.linkedinDescription !== createDefaultLinkedinShareMessage(this.disciplineName);
    },
    trackShareButton(): void {
      Analytics.trackEvent(EVENT_LABELS.buttonClicked, {
        postgrad_microcertificate_linkedin_modal: true,
        postgrad_microcertificate_linkedin_modal_action: 'share_linkedin',
        course: this.course.name,
        category: getCourseCategory(this.course),
        visibility_option: Object.values(this.whoCanSee)[0],
        has_changed_default_text: this.verifyIfTextWasChanged(),
        ...this.earlyGraduationTrackingProps,
      });
    },
    trackSharedInLinkedin(label: string): void {
      Analytics.trackEvent(EVENT_LABELS[label], {
        course: this.course.name,
        category: getCourseCategory(this.course),
        visibility_option: Object.values(this.whoCanSee)[0],
        has_changed_default_text: this.verifyIfTextWasChanged(),
        ...this.earlyGraduationTrackingProps,
      });
    },
  },
});
